import React, { useState } from "react";
import PropTypes from "prop-types";
import { useForm } from "react-hook-form";
import Button from "components/Button";
import axios from "axios";
import { useApiEndpoint } from "ApiEndpointContext";
import { ReactComponent as LoadingIcon } from "images/icons/loading.svg";
import { mutateDocuments } from "hooks/useDocuments";

const GENERIC_ERROR =
  "Un problème est survenu lors de la génération de l'attestation sur l'honneur, veuillez contacter votre administrateur. (Champs obligatoires : FOST et Date de signature du devis)";

function Champ({ label, value }) {
  return (
    <div className="border-b p-2">
      <p className="text-xs text-[#212121]">{label}</p>
      <p>{value || "Aucun"}</p>
    </div>
  );
}
Champ.propTypes = {
  label: PropTypes.any,
  value: PropTypes.any,
};

export default function AhDialog({ onClose, id, fetchFilesData, data }) {
  const apiEndpoint = useApiEndpoint();
  const { handleSubmit } = useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({});

  const onSubmitStep = async (data) => {
    // Store data from the current step
    setFormData({ ...formData, ...data });

    try {
      setLoading(true);

      const apiResult = await axios.get(
        `${apiEndpoint}/api/generationah/${id}`,
        { withCredentials: true },
      );
      if (apiResult.data?.error_message) {
        setError(apiResult.data.error_message);
        return;
      }
      // Check if both API calls were successful
      if (apiResult.data) {
        // Trigger a revalidation (refetch)
        mutateDocuments(id);
        setSuccess(true);
        onClose();
      } else {
        // Handle the case where one or both API calls failed
        setError(GENERIC_ERROR);
      }
    } catch (error) {
      console.error("error", error);
      setError(error.message || GENERIC_ERROR);
    } finally {
      setLoading(false);
      fetchFilesData();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-center overflow-scroll">
      <div
        className={`absolute top-0 left-0 right-0 bottom-0 bg-white bg-opacity-90 flex items-center justify-center h-full`}
      >
        <form
          onSubmit={handleSubmit(onSubmitStep)}
          className={`w-[80%] max-w-[689px] mt-20 p-8 grid- bg-white rounded border relative gap-3`}
        >
          <button
            type="button"
            onClick={onClose}
            className="text-xs underline absolute right-4 top-4 z-20"
          >
            Fermer
          </button>
          <div className="">
            <h2 className="text-xl font-semibold text-main-color mb-3">
              Est-ce que les informations ci-dessous sont correctes ?
            </h2>
          </div>

          <div className="col-span-2 flex flex-col gap-1">
            <div className="bg-main-color text-white px-4 py-3 rounded mb-2 font-semibold flex justify-between">
              <span>Attestation sur l’honneur</span>
            </div>

            <Champ
              label="Date de signature du devis"
              value={
                data.data.attributes.chantiers.data[0].attributes
                  .date_signature_devis
              }
            />
            <Champ
              label="Date d’édition de la facture"
              value={
                data.data.attributes.chantiers.data[0].attributes
                  .date_edition_facture
              }
            />
            <Champ
              label="Nom"
              value={data.data.attributes.beneficiaire.data?.attributes?.nom}
            />
            <Champ
              label="Prénom"
              value={data.data.attributes.beneficiaire.data?.attributes?.prenom}
            />
            <Champ
              label="Adresse du chantier ou Parcelle cadastrale"
              value={data.data.attributes.chantiers.data[0].attributes.adresse}
            />
            <Champ
              label="Code postal du chantier"
              value={
                data.data.attributes.chantiers.data[0].attributes.code_postal
              }
            />
            <Champ
              label="Ville du chantier"
              value={data.data.attributes.chantiers.data[0].attributes.ville}
            />
            <Champ
              label="Bâtiment construit depuis plus de 2 ans"
              value={
                data.data.attributes.chantiers.data[0].attributes
                  .plus_de_deux_ans
                  ? "Oui"
                  : "Non"
              }
            />
            <Champ
              label="Type de logement"
              value={
                data.data.attributes.chantiers.data[0].attributes
                  .type_de_logement
              }
            />
            <Champ
              label="Information sur le professionnel"
              value={
                data.data.attributes.entreprise_de_travaux?.data?.attributes
                  ?.raison_sociale
              }
            />
            <Champ
              label="FOST"
              value={data.data.attributes.fosts.data[0].attributes.numero_fost}
            />
          </div>

          <div className="col-span-2">
            <Button
              label={"Générer le document"}
              className="mx-auto mt-4"
              variant="contained"
              color={"blue"}
              type="submit"
              icon={loading ? <LoadingIcon /> : ""}
              disabled={loading}
            />

            {error && (
              <p className="text-red-500 text-xs mx-auto mt-6">{error}</p>
            )}

            {success && (
              <p className="text-green text-xs mx-auto mt-3">succès !</p>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
AhDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  id: PropTypes.number.isRequired,
  fetchFilesData: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
};
